/**
 * Стили графика.
 *
 * Каждый стиль описывает себя целиком: какой серией рисуется, какими
 * настройками и как из свечи получается точка ряда. График не знает, что
 * такое «пустые свечи», — он берёт описание и применяет его. Тот же
 * приём, что у индикаторов: добавление стиля не требует правок графика.
 *
 * Здесь только то, что библиотека действительно умеет. Свечи объёма
 * (ширина столбца по объёму), область HLC и мин-макс требуют собственного
 * рисовальщика серии — их нет и притворяться, что есть, нельзя. Ренко,
 * Каги и линейный прорыв не ложатся на ось времени в принципе: у них бар
 * рождается от движения цены, а не от истечения интервала.
 */

import {
  AreaSeries,
  BarSeries,
  BaselineSeries,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  LineType,
  type ISeriesApi,
  type SeriesDataItemTypeMap,
  type SeriesDefinition,
  type SeriesType,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle } from '../state/store.js';
import { palette } from './theme.js';

export type ChartStyleId =
  | 'bars'
  | 'candles'
  | 'hollow'
  | 'heikin-ashi'
  | 'line'
  | 'line-dots'
  | 'line-steps'
  | 'area'
  | 'baseline'
  | 'columns';

/**
 * Точка любой поддерживаемой серии.
 *
 * Берём объединение из самой библиотеки: у свечи это OHLC, у линии —
 * значение, у столбца — значение с цветом. Свой тип здесь означал бы
 * приведение при каждой заливке данных.
 */
export type StyleDatum = SeriesDataItemTypeMap[SeriesType];

export interface ChartStyle {
  readonly id: ChartStyleId;
  readonly name: string;
  /** Группа в меню: свечные, линейные, площадные. */
  readonly group: 'bars' | 'lines' | 'areas';
  readonly definition: SeriesDefinition<SeriesType>;
  readonly options: Record<string, unknown>;
  /**
   * Пересчёт одной свечи с оглядкой на предыдущую пересчитанную.
   *
   * Нужен только Хейкен Аши: он не оформление, а другой ряд, где каждая
   * свеча зависит от предыдущей. Записан именно шагом, а не обработкой
   * всего ряда, чтобы живой бар пересчитывался одной операцией, а не
   * перебором тысяч баров на каждый тик.
   */
  readonly step?: (previous: Candle | null, candle: Candle) => Candle;
  readonly toDatum: (candle: Candle) => StyleDatum;
}

const seconds = (time: number): UTCTimestamp => (time / 1000) as UTCTimestamp;

const ohlc = (candle: Candle): StyleDatum => ({
  time: seconds(candle.time),
  open: candle.open,
  high: candle.high,
  low: candle.low,
  close: candle.close,
});

const close = (candle: Candle): StyleDatum => ({
  time: seconds(candle.time),
  value: candle.close,
});

const CANDLE_COLORS = {
  upColor: palette.up,
  downColor: palette.down,
  borderUpColor: palette.up,
  borderDownColor: palette.down,
  wickUpColor: palette.up,
  wickDownColor: palette.down,
};

const LINE_BASE = {
  color: palette.accent,
  lineWidth: 2,
  priceLineVisible: true,
  lastValueVisible: true,
};

/**
 * Хейкен Аши.
 *
 * Сглаженные свечи: открытие — середина предыдущей сглаженной свечи,
 * закрытие — среднее OHLC текущей. Первая свеча опорной не имеет, её
 * открытие берётся как середина исходной — иначе весь ряд уехал бы от
 * единственного неизвестного значения.
 *
 * Важно помнить, чем это НЕ является: цены здесь производные. Уровень,
 * снятый с такого графика, к реальным сделкам отношения не имеет.
 */
function heikinAshiStep(previous: Candle | null, candle: Candle): Candle {
  const haClose = (candle.open + candle.high + candle.low + candle.close) / 4;
  const haOpen = previous
    ? (previous.open + previous.close) / 2
    : (candle.open + candle.close) / 2;

  return {
    time: candle.time,
    open: haOpen,
    close: haClose,
    high: Math.max(candle.high, haOpen, haClose),
    low: Math.min(candle.low, haOpen, haClose),
    volume: candle.volume,
  };
}

/** Прогон шага по всему ряду. */
export function applyStep(
  step: (previous: Candle | null, candle: Candle) => Candle,
  candles: readonly Candle[],
): Candle[] {
  const result: Candle[] = [];
  let previous: Candle | null = null;
  for (const candle of candles) {
    previous = step(previous, candle);
    result.push(previous);
  }
  return result;
}

export const CHART_STYLES: readonly ChartStyle[] = [
  {
    id: 'bars',
    name: 'Бары',
    group: 'bars',
    definition: BarSeries,
    options: { upColor: palette.up, downColor: palette.down, thinBars: false },
    toDatum: ohlc,
  },
  {
    id: 'candles',
    name: 'Японские свечи',
    group: 'bars',
    definition: CandlestickSeries,
    options: CANDLE_COLORS,
    toDatum: ohlc,
  },
  {
    id: 'hollow',
    name: 'Пустые свечи',
    group: 'bars',
    definition: CandlestickSeries,
    // Растущая свеча — только контур. Заливка остаётся у падающей,
    // поэтому направление читается силуэтом, а не цветом.
    options: { ...CANDLE_COLORS, upColor: 'rgba(0, 0, 0, 0)' },
    toDatum: ohlc,
  },
  {
    id: 'heikin-ashi',
    name: 'Хейкен Аши',
    group: 'bars',
    definition: CandlestickSeries,
    options: CANDLE_COLORS,
    step: heikinAshiStep,
    toDatum: ohlc,
  },
  {
    id: 'line',
    name: 'Линия',
    group: 'lines',
    definition: LineSeries,
    options: LINE_BASE,
    toDatum: close,
  },
  {
    id: 'line-dots',
    name: 'Линия с точками',
    group: 'lines',
    definition: LineSeries,
    options: { ...LINE_BASE, pointMarkersVisible: true },
    toDatum: close,
  },
  {
    id: 'line-steps',
    name: 'Ступенчатая линия',
    group: 'lines',
    definition: LineSeries,
    options: { ...LINE_BASE, lineType: LineType.WithSteps },
    toDatum: close,
  },
  {
    id: 'area',
    name: 'Область',
    group: 'areas',
    definition: AreaSeries,
    options: {
      lineColor: palette.accent,
      topColor: 'rgba(227, 164, 85, 0.28)',
      bottomColor: 'rgba(227, 164, 85, 0.02)',
      lineWidth: 2,
    },
    toDatum: close,
  },
  {
    id: 'baseline',
    name: 'Базовая линия',
    group: 'areas',
    definition: BaselineSeries,
    // Основание — первая цена ряда: выше неё рост, ниже падение.
    options: {
      baseValue: { type: 'price', price: 0 },
      topLineColor: palette.up,
      topFillColor1: 'rgba(63, 191, 135, 0.28)',
      topFillColor2: 'rgba(63, 191, 135, 0.02)',
      bottomLineColor: palette.down,
      bottomFillColor1: 'rgba(240, 97, 111, 0.02)',
      bottomFillColor2: 'rgba(240, 97, 111, 0.28)',
    },
    toDatum: close,
  },
  {
    id: 'columns',
    name: 'Столбцы',
    group: 'areas',
    definition: HistogramSeries,
    options: { priceLineVisible: false },
    toDatum: (candle) => ({
      time: seconds(candle.time),
      value: candle.close,
      color: candle.close >= candle.open ? palette.upSoft : palette.downSoft,
    }),
  },
];

const BY_ID = new Map<string, ChartStyle>(CHART_STYLES.map((style) => [style.id, style]));

/** Стиль по умолчанию: японские свечи. */
export const DEFAULT_CHART_STYLE: ChartStyleId = 'candles';

/** Неизвестный стиль из профиля не должен оставлять график пустым. */
export function findStyle(id: string): ChartStyle {
  return BY_ID.get(id) ?? BY_ID.get(DEFAULT_CHART_STYLE)!;
}

export const STYLE_GROUPS: ReadonlyArray<{ id: ChartStyle['group']; label: string }> = [
  { id: 'bars', label: 'Бары и свечи' },
  { id: 'lines', label: 'Линии' },
  { id: 'areas', label: 'Области' },
];

/**
 * Базовая линия отсчитывается от первой цены ряда.
 *
 * Ноль в качестве основания сделал бы весь график «растущим»: цена
 * инструмента всегда выше нуля.
 */
export function styleOptionsFor(style: ChartStyle, candles: readonly Candle[]): Record<string, unknown> {
  if (style.id !== 'baseline') return style.options;
  const first = candles[0]?.close;
  if (first === undefined) return style.options;
  return { ...style.options, baseValue: { type: 'price', price: first } };
}

export type AnySeries = ISeriesApi<SeriesType>;
